import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { Divider } from 'Src/feature/divider/Divider';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { useToast } from 'Src/feature/toasts/useToast';

import { useOloConfig } from './useOloConfig';

import styles from './OloConfigModal.module.scss';

const sectionId = 'OloConfigModal';

interface OloConfigModalProps {
  open: boolean;
  onDone: () => void;
}

interface OloConfigModalContentProps {
  onDone: () => void;
}

interface FormModel {
  acceptOrders: boolean;
  takeoutLeadTimeMinOverride: number;
  deliveryLeadTimeMinOverride: number;
}

const { Field, Provider: FormProvider } = getTypedFormComponents<FormModel>();

const OloConfigModalContent: FC<OloConfigModalContentProps> = ({ onDone }) => {
  const { t } = useTranslation();
  const oloConfig = useOloConfig.query.get();
  const updateThrottlingInfo = useOloConfig.mutate.updateThrottlingInfo();
  const toast = useToast();

  const form = useForm<FormModel>({
    defaultValues: {
      acceptOrders: !oloConfig.data ? true : oloConfig.data.paused,
      takeoutLeadTimeMinOverride: !oloConfig.data ? 0 : oloConfig.data.takeoutLeadTimeMinOverride,
      deliveryLeadTimeMinOverride: !oloConfig.data ? 0 : oloConfig.data.deliveryLeadTimeMinOverride,
    },
  });

  useEffect(() => {
    if (!oloConfig.data) return;
    if (form.state.isDirty) return;
    form.action.reset({
      acceptOrders: !oloConfig.data.paused,
      takeoutLeadTimeMinOverride: oloConfig.data.takeoutLeadTimeMinOverride,
      deliveryLeadTimeMinOverride: oloConfig.data.deliveryLeadTimeMinOverride,
    });
  }, [form.action, form.state.isDirty, oloConfig.data]);

  const onSave = async () => {
    const { values, isValid } = await form.action.submit();
    if (!isValid) return;
    updateThrottlingInfo.mutate({
      throttlingInfo: {
        paused: !values.acceptOrders,
        takeoutLeadTimeMinOverride: values.takeoutLeadTimeMinOverride,
        deliveryLeadTimeMinOverride: values.deliveryLeadTimeMinOverride,
      },
    }, {
      onSuccess: () => onDone(),
      onError: (err) => toast.show.serviceError(err, { id: `${sectionId}.onSave` }),
    });
  };

  const leadTimeOverrides = [
    { value: 0, label: t('olo.labels.leadTimeReset') },
    ...[15, 30, 45, 60, 75, 90, 105, 120, 135, 150, 165, 180].map((value) => (
      { value, label: t('olo.labels.leadTimeMinuteOption', { value }) }
    )),
  ];

  const formTakeoutLeadTimeMinOverride = form.state.watch('takeoutLeadTimeMinOverride');
  const formDeliveryLeadTimeMinOverride = form.state.watch('deliveryLeadTimeMinOverride');

  return (
    <>
      <ModalContent>
        <FormProvider usedForm={form}>
          <Flex className={styles.form_fields}>
            <Flex className={styles.field} horizontal justifyContent="space-between" alignItems="center">
              <Copy value={t('olo.labels.acceptOnlineOrders')} />
              <Field
                type="switch"
                name="acceptOrders"
              />
            </Flex>
            <Flex className={styles.field} horizontal justifyContent="space-between" alignItems="center">
              <Copy value={t('olo.labels.takeoutLeadTimeOverride')} />
              <Flex horizontal>
                {formTakeoutLeadTimeMinOverride !== 0 && (
                  <CTA
                    variant="text"
                    label={t('olo.labels.resetToDefault', { value: oloConfig.data?.takeoutLeadTimeMin })}
                    color="primary"
                    icon={{ name: 'laps', position: 'start' }}
                    onClick={() => form.action.setValue('takeoutLeadTimeMinOverride', 0)}
                  />
                )}
                <Divider orientation="vertical" flexItem />
                <Field
                  type="select"
                  name="takeoutLeadTimeMinOverride"
                  options={leadTimeOverrides}
                  variant="no-background"
                />
              </Flex>
            </Flex>
            <Flex className={styles.field} horizontal justifyContent="space-between" alignItems="center">
              <Copy value={t('olo.labels.deliveryLeadTimeOverride')} />
              <Flex horizontal>
                {formDeliveryLeadTimeMinOverride !== 0 && (
                  <CTA
                    variant="text"
                    label={t('olo.labels.resetToDefault', { value: oloConfig.data?.deliveryLeadTimeMin })}
                    color="primary"
                    icon={{ name: 'laps', position: 'start' }}
                    onClick={() => form.action.setValue('deliveryLeadTimeMinOverride', 0)}
                  />
                )}
                <Divider orientation="vertical" flexItem />
                <Field
                  type="select"
                  name="deliveryLeadTimeMinOverride"
                  options={leadTimeOverrides}
                  variant="no-background"
                />
              </Flex>
            </Flex>
          </Flex>
        </FormProvider>
      </ModalContent>
      <ModalActions>
        <ModalAction
          color="secondary"
          label={t('labels.cancel')}
          onClick={onDone}
        />
        <ModalAction
          color="primary"
          label={t('labels.confirm')}
          onClick={onSave}
        />
      </ModalActions>
    </>
  );
};

// eslint-disable-next-line react/no-multi-comp
const OloConfigModal: FC<OloConfigModalProps> = ({ open, onDone }) => {
  const { t } = useTranslation();
  
  if (!open) return;

  return (
    <Modal
      open={open}
      onBackDropClick={onDone}
      variant="grow"
    >
      <ModalHeader>
        <ModalTitle copy={t('olo.labels.configTitle')} />
      </ModalHeader>
      <OloConfigModalContent onDone={onDone} />
    </Modal>
  );
};

export { OloConfigModal };
